import React, { useEffect, useState, useRef } from 'react';
import { Send } from 'lucide-react';
import { Button, TextInput, Loader, Alert } from '@mantine/core';
import { Message } from './Message';
import { RecommendedProducts } from './RecommendedProducts';
import { generateCatalogResponse, getOpenAIInstance } from '../services/openai';
import { sendMessageToApi } from '../services/api';
export const ChatInterface = ({
  conversation,
  addMessage,
  recommendedProducts
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [displayMessages, setDisplayMessages] = useState([]);
  const conversationIdRef = useRef(null);
  const [apiError, setApiError] = useState(null);
  // Only update display messages when conversation changes (not on every render)
  useEffect(() => {
    // Check if this is a different conversation
    if (conversation?.id !== conversationIdRef.current) {
      setDisplayMessages(conversation?.messages || []);
      conversationIdRef.current = conversation?.id;
    }
  }, [conversation?.id, conversation?.messages]);
  const handleSubmit = async e => {
    e.preventDefault();
    if (inputValue.trim() === '' || isLoading) return;
    // Store the input value before clearing it
    const messageText = inputValue.trim();
    // Create a new user message with a unique ID
    const userMessage = {
      id: `local-${Date.now()}`,
      type: 'user',
      content: messageText
    };
    // Immediately add the user message to display messages
    setDisplayMessages(prevMessages => [...prevMessages, userMessage]);
    // Clear the input field
    setInputValue('');
    // Add the message to the global state
    addMessage({
      type: 'user',
      content: messageText
    });
    // Set loading state
    setIsLoading(true);
    // Clear any previous errors
    setApiError(null);
    try {
      // Get all messages for context
      const messages = [...conversation.messages, {
        type: 'user',
        content: messageText
      }];
      // Send message to API and get response
      const response = await sendMessageToApi(messages);
      // Create a new assistant message with a unique ID
      const assistantMessage = {
        id: `local-${Date.now() + 1}`,
        type: 'assistant',
        content: response
      };
      // Add the assistant message to the display messages
      setDisplayMessages(prevMessages => [...prevMessages, assistantMessage]);
      // Add the assistant message to the global state
      addMessage({
        type: 'assistant',
        content: response
      });
    } catch (error) {
      console.error('Error generating response:', error);
      // Set error for display
      setApiError(`Error: ${error.message || 'Failed to get response from API'}`);
      // Create an error message
      const errorMessage = {
        id: `local-${Date.now() + 1}`,
        type: 'assistant',
        content: 'Sorry, I encountered an error while processing your request. Please try again later or check the API settings.'
      };
      // Add the error message to the display messages
      setDisplayMessages(prevMessages => [...prevMessages, errorMessage]);
      // Add the error message to the global state
      addMessage({
        type: 'assistant',
        content: errorMessage.content
      });
    } finally {
      setIsLoading(false);
      // Focus the input field after response
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };
  const handleInputChange = e => {
    setInputValue(e.target.value);
  };
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [displayMessages, isLoading]);
  return <div className="flex-1 flex flex-col h-full bg-white" data-id="element-81">
      <div className="flex-1 overflow-y-auto p-2 sm:p-4" data-id="element-82">
        <div className="max-w-3xl mx-auto" data-id="element-83">
          {/* Add recommended products at the top */}
          <RecommendedProducts products={recommendedProducts} data-id="element-84" />
          {apiError && <Alert color="red" title="API Error" className="mb-4" data-id="element-85">
              {apiError}
            </Alert>}
          <div className="space-y-1" data-id="element-86">
            {displayMessages.map(message => <Message key={message.id} message={message} data-id="element-87" />)}
            {isLoading && <div className="flex justify-start" data-id="element-88">
                <div className="bg-gray-100 text-gray-800 rounded-lg rounded-tl-none p-3 max-w-[85%] sm:max-w-[80%] flex items-center" data-id="element-89">
                  <Loader size="sm" className="mr-2" data-id="element-90" />
                  <span className="text-sm" data-id="element-91">Thinking...</span>
                </div>
              </div>}
          </div>
          <div ref={messagesEndRef} data-id="element-92" />
        </div>
      </div>
      <div className="border-t border-gray-200 p-2 sm:p-4 bg-white" data-id="element-93">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto" data-id="element-94">
          <div className="flex items-center" data-id="element-95">
            <TextInput className="flex-1" placeholder="Ask about products..." value={inputValue} onChange={handleInputChange} radius="md" size="sm" ref={inputRef} styles={{
            input: {
              fontSize: '14px'
            }
          }} disabled={isLoading} data-id="element-96" />
            <Button type="submit" className="ml-2 bg-blue-600 hover:bg-blue-700" radius="md" size="sm" px="xs" loading={isLoading} data-id="element-97">
              <Send size={16} data-id="element-98" />
            </Button>
          </div>
        </form>
      </div>
    </div>;
};